import {Component, OnDestroy, OnInit} from '@angular/core';
import {MatListModule} from '@angular/material/list';
import {CommonModule} from '@angular/common';
import {MatLineModule} from '@angular/material/core';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {Subscription} from 'rxjs';
import {MatDialog} from '@angular/material/dialog';
import {MatBottomSheetRef} from '@angular/material/bottom-sheet';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {FilterService} from '../api/services/filter.service';
import {MapService} from '../api/services/map.service';
import {CreateFilterDialogComponent} from './create-filter-dialog.component';
import {ConfirmationDialogComponent} from './confirmation-dialog.component';
import {FilterDto} from '@maporium-workspace/shared';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'maporium-filter-list',
  styleUrls: ['./filter-list.bottomsheet.component.scss'],
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0">
        <span class="icon-smap-object-filter me-2"></span>{{ 'FILTER_LIST.HEADING' | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" (click)="dismiss()" [matTooltip]="'SHARE_DIALOG.CLOSE_TOOLTIP' | translate"></span>
    </div>
    <mat-nav-list class="list-wrapper">
      <a mat-list-item *ngFor="let filter of filters"
         (click)="selectFilter(filter); $event.preventDefault(); $event.stopPropagation()"
         class="d-flex justify-content-evenly p-0">
        <span class="row w-100 align-items-center m-0">
          <span class="col-2 align-items-center d-flex">
            <mat-icon class="material-symbols-rounded"
                      *ngIf="!filter.excludeFromReadOnly"
                      [matTooltip]="'FILTER_LIST.VISIBLE_TOOLTIP' | translate">visibility</mat-icon>
            <mat-icon class="material-symbols-rounded"
                      [matTooltip]="'FILTER_LIST.HIDDEN_TOOLTIP' | translate"
                      *ngIf="filter.excludeFromReadOnly">visibility_off</mat-icon>
          </span>
          <span class="col-6 pe-5"
                [matTooltip]="filter.description || filter.name"
                matTooltipPosition="right">
            <span matLine class="d-flex align-items-center">
              <mat-icon *ngIf="isFilterActive(filter.id)"
                        class="material-symbols-rounded me-2 filter-active-icon">check</mat-icon>
              {{filter.name}}
              <mat-icon *ngIf="filter.isGlobal"
                        class="material-symbols-rounded ms-2 global-icon"
                        [matTooltip]="'FILTER_DIALOG.GLOBAL_FILTER' | translate">public</mat-icon>
            </span>
          </span>
          <span class="col-4 d-flex justify-content-end">
            <button mat-icon-button
                    [matTooltip]="'FILTER_LIST.EDIT_TOOLTIP' | translate"
                    (click)="edit(filter);$event.preventDefault(); $event.stopPropagation()">
              <mat-icon class="material-symbols-rounded">edit</mat-icon>
            </button>

            <button mat-icon-button
                    [matTooltip]="'FILTER_LIST.DELETE_TOOLTIP' | translate"
                    (click)="askForConfirmation(filter);$event.preventDefault(); $event.stopPropagation()">
              <mat-icon class="material-symbols-rounded">delete</mat-icon>
            </button>
          </span>
        </span>
      </a>
    </mat-nav-list>
    <mat-divider></mat-divider>
    <div class="py-2" style="padding-left: 10px;">
        <button class="dialog-action-btn dialog-action-btn-primary"
                [matTooltip]="'FILTER_LIST.CREATE_TOOLTIP' | translate"
                (click)="createNew(); $event.preventDefault();">
          {{ 'FILTER_LIST.CREATE_FILTER' | translate }}
        </button>
    </div>
  `,
  standalone: true,
  imports: [
    MatListModule,
    CommonModule,
    MatLineModule,
    MatIconModule,
    MatButtonModule,
    MatSnackBarModule,
    MatTooltipModule,
    TranslatePipe,
  ]
})
export class FilterListBottomsheetComponent implements OnInit, OnDestroy {
  public filters: FilterDto[] = [];
  private subSink: Subscription = new Subscription();

  constructor(
    private matDialog: MatDialog,
    private mapService: MapService,
    private snackBar: MatSnackBar,
    private filterService: FilterService,
    private translate: TranslateService,
    private bottomSheetRef: MatBottomSheetRef<FilterListBottomsheetComponent>
  ) {}

  ngOnInit() {
    this.loadFilters();

    // Subscribe to filter changes
    this.subSink.add(
      this.filterService.filtersChangedSubject.subscribe(() => {
        this.loadFilters();
      })
    );
  }

  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

  private loadFilters() {
    const mapId = this.mapService.getCurrentSelectedMapFromStore().id;
    this.subSink.add(
      this.filterService.getAll(mapId).subscribe((filters) => {
        this.filters = filters;
      })
    );
  }

  selectFilter(filter: FilterDto) {
    this.filterService.toggleFilter(filter);
    this.bottomSheetRef.dismiss(filter);
  }

  isFilterActive(filterId: string): boolean {
    return this.filterService.isFilterActive(filterId);
  }

  createNew() {
    const ref = this.matDialog.open(CreateFilterDialogComponent);
    ref.afterClosed().subscribe((filter: FilterDto) => {
      if (filter) {
        this.loadFilters();
      }
    });
  }

  edit(filter: FilterDto) {
    const ref = this.matDialog.open(CreateFilterDialogComponent, { data: filter });
    ref.afterClosed().subscribe((updatedFilter: FilterDto) => {
      if (updatedFilter) {
        this.loadFilters();
      }
    });
  }

  delete(filter: FilterDto) {
    this.filterService.delete(filter.id).subscribe(() => {
      this.filters = this.filters.filter((f) => f.id !== filter.id);
      this.snackBar.open(this.translate.instant('FILTER_LIST.DELETED_SUCCESS'), this.translate.instant('FILTER_LIST.DISMISS'), { duration: 3000 });
    });
  }

  dismiss() {
    this.bottomSheetRef.dismiss();
  }

  askForConfirmation(filter: FilterDto) {
    const ref = this.matDialog.open(ConfirmationDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: {
        title: this.translate.instant('FILTER_LIST.DELETE_FILTER_TITLE'),
        message: this.translate.instant('FILTER_LIST.DELETE_FILTER_MESSAGE', { name: filter.name }),
        confirmText: this.translate.instant('DASHBOARD_MSG.CONFIRM.DELETE_CONFIRM')
      }
    });
    ref.afterClosed().subscribe((res) => {
      if (res) {
        this.delete(filter);
      }
    });
  }
}
